<script setup>
import { computed, onMounted, onUnmounted, shallowRef, useTemplateRef } from "vue";
import { iconHref } from "~/utils/icons.js";

/**
 * GlSorting — mirrors the @gitlab/ui GlSorting component API.
 *
 * Renders a sort-field dropdown toggle (with a checkmark on the active item)
 * and a sort-direction toggle button side-by-side, matching the upstream
 * GlButtonGroup + GlCollapsibleListbox + GlButton composition.
 *
 * @see https://gitlab-org.gitlab.io/gitlab-ui/?path=/story/base-sorting--default
 */
const { isAscending, sortBy, sortDirectionToolTip, sortOptions, text } = defineProps({
  isAscending: { default: false, type: Boolean },
  sortBy: { default: null, type: [String, Number] },
  sortDirectionToolTip: { default: null, type: String },
  sortOptions: { default: () => [], type: Array },
  text: { default: "", type: String }
});

const emit = defineEmits(["sortByChange", "sortDirectionChange"]);

const open = shallowRef(false);
const dropdownRef = useTemplateRef("dropdown");

const toggleText = computed(() => {
  if (text) return text;
  const option = sortOptions.find((o) => o.value === sortBy);
  return option?.text ?? "";
});

const sortDirectionIcon = computed(() => (isAscending ? "sort-lowest" : "sort-highest"));

const sortDirectionText = computed(() => {
  if (sortDirectionToolTip) return sortDirectionToolTip;
  return isAscending ? "Sort direction: ascending" : "Sort direction: descending";
});

function toggle() {
  open.value = !open.value;
}

function selectOption(value) {
  emit("sortByChange", value);
  open.value = false;
}

function toggleDirection() {
  emit("sortDirectionChange", !isAscending);
}

function onClickOutside(event) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    open.value = false;
  }
}

onMounted(() => document.addEventListener("click", onClickOutside));
onUnmounted(() => document.removeEventListener("click", onClickOutside));
</script>

<template>
  <div class="gl-sorting" ref="dropdown" role="group">
    <div class="gl-sorting-dropdown-container">
      <button type="button" class="gl-sorting-toggle" :aria-expanded="open" @click.stop="toggle">
        {{ toggleText }}
        <svg :class="['gl-sorting-chevron', open && 'is-open']" aria-hidden="true">
          <use :href="iconHref('chevron-down')" />
        </svg>
      </button>

      <ul v-show="open" class="gl-sorting-menu" role="listbox" :aria-label="'Sort by'">
        <li v-for="option in sortOptions" :key="option.value" role="option" :aria-selected="sortBy === option.value">
          <button
            type="button"
            :class="['gl-sorting-item', sortBy === option.value && 'is-active']"
            @click="selectOption(option.value)"
          >
            <svg :class="['gl-sorting-check', sortBy !== option.value && 'invisible']" aria-hidden="true">
              <use :href="iconHref('check')" />
            </svg>
            {{ option.text }}
          </button>
        </li>
      </ul>
    </div>

    <button
      type="button"
      class="gl-sorting-direction-button"
      :title="sortDirectionText"
      :aria-label="sortDirectionText"
      @click="toggleDirection"
    >
      <svg class="h-4 w-4" aria-hidden="true">
        <use :href="iconHref(sortDirectionIcon)" />
      </svg>
    </button>
  </div>
</template>
